import { type Vector2 } from './Vector2';

/**
 * An axis aligned rectangle in world units.
 *
 * `x` and `y` are the minimum corner, so in the engine's Y-up world they name the left and bottom
 * edges. Mutable and allocation-friendly for the same reason {@link Vector2} is: bounds are recomputed
 * every frame by culling, hit-testing and debug draw.
 */
export class Rect {
  /** Left edge. */
  x: number;
  /** Bottom edge, because the world is Y-up. */
  y: number;
  /** Extent along X; negative widths are not supported. */
  width: number;
  /** Extent along Y; negative heights are not supported. */
  height: number;

  constructor(x = 0, y = 0, width = 0, height = 0) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  /** A new rectangle of the given size centered on a point, the way sprites and cameras are placed. */
  static fromCenter(centerX: number, centerY: number, width: number, height: number): Rect {
    return new Rect(centerX - width / 2, centerY - height / 2, width, height);
  }

  /** Left edge. */
  get left(): number {
    return this.x;
  }

  /** Right edge. */
  get right(): number {
    return this.x + this.width;
  }

  /** Bottom edge. */
  get bottom(): number {
    return this.y;
  }

  /** Top edge. */
  get top(): number {
    return this.y + this.height;
  }

  /** Horizontal midpoint. */
  get centerX(): number {
    return this.x + this.width / 2;
  }

  /** Vertical midpoint. */
  get centerY(): number {
    return this.y + this.height / 2;
  }

  /** Overwrites the minimum corner and the size. */
  set(x: number, y: number, width: number, height: number): this {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
    return this;
  }

  /** Repositions the rectangle so it is centered on the given point, keeping its size. */
  setCenter(centerX: number, centerY: number): this {
    this.x = centerX - this.width / 2;
    this.y = centerY - this.height / 2;
    return this;
  }

  /** Copies position and size from `source`. */
  copyFrom(source: Rect): this {
    return this.set(source.x, source.y, source.width, source.height);
  }

  /** An independent copy of this rectangle. */
  clone(): Rect {
    return new Rect(this.x, this.y, this.width, this.height);
  }

  /** Reports whether a point lies inside the rectangle. Edges count as inside. */
  contains(x: number, y: number): boolean {
    return x >= this.x && x <= this.right && y >= this.y && y <= this.top;
  }

  /** Reports whether a point lies inside the rectangle. Edges count as inside. */
  containsPoint(point: Vector2): boolean {
    return this.contains(point.x, point.y);
  }

  /** Reports whether this rectangle overlaps `other`. Touching edges count as overlapping. */
  intersects(other: Rect): boolean {
    return this.x <= other.right && this.right >= other.x && this.y <= other.top && this.top >= other.y;
  }
}
